/* GroundingFlow 2.0 — "Night Lab" interactions (no dependencies) */
(() => {
  'use strict';
  const d = document;
  const w = window;
  const $ = (s, r = d) => r.querySelector(s);
  const $$ = (s, r = d) => Array.from(r.querySelectorAll(s));
  const reduce = w.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const canHover = w.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const conn = navigator.connection || {};
  const saveData = !!conn.saveData;
  const slowNet = /(^|-)2g$|3g/.test(conn.effectiveType || '');
  const hasIO = 'IntersectionObserver' in w;
  const hasRO = 'ResizeObserver' in w;
  const money = (cents) => {
    const fmt = (w.Shopify && w.Shopify.currency && w.Shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat(d.documentElement.lang || 'en-US', { style: 'currency', currency: fmt }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  };
  const moneyShort = (cents) => money(cents).replace(/\.00(?!\d)/, '');
  const I18N = w.GF_I18N || {};
  const LANG = d.documentElement.lang || 'en-US';
  const once = (el, key) => {
    if (el.dataset[key]) return false;
    el.dataset[key] = '1';
    return true;
  };

  /* Runs a draw loop only while the element is on screen and the tab is visible. */
  function loop(el, frame) {
    let raf = 0;
    let onScreen = false;
    const tick = (now) => {
      frame(now);
      raf = requestAnimationFrame(tick);
    };
    const sync = () => {
      const run = onScreen && !d.hidden && !reduce;
      if (run && !raf) raf = requestAnimationFrame(tick);
      if (!run && raf) { cancelAnimationFrame(raf); raf = 0; }
    };
    if (hasIO) {
      new IntersectionObserver((es) => { onScreen = es[0].isIntersecting; sync(); }, { rootMargin: '120px 0px' }).observe(el);
    } else {
      onScreen = true;
    }
    d.addEventListener('visibilitychange', sync);
    sync();
  }

  /* Keeps a canvas backing store matched to its CSS size. */
  function fitCanvas(c, onResize) {
    const fit = () => {
      const dpr = Math.min(2, w.devicePixelRatio || 1);
      const cw = Math.max(1, Math.round(c.clientWidth * dpr));
      const ch = Math.max(1, Math.round(c.clientHeight * dpr));
      if (c.width !== cw || c.height !== ch) { c.width = cw; c.height = ch; }
      onResize(cw, ch, dpr);
    };
    if (hasRO) new ResizeObserver(fit).observe(c); else w.addEventListener('resize', fit);
    fit();
  }

  /* ---------- Reveal on scroll ---------- */
  function initReveal(root = d) {
    const els = $$('.gf-reveal:not(.is-in)', root);
    if (!hasIO || reduce) { els.forEach((e) => e.classList.add('is-in')); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); } });
    }, { rootMargin: '0px 0px -6% 0px', threshold: 0.06 });
    els.forEach((e) => io.observe(e));
    // Safety net: never leave content hidden.
    w.setTimeout(() => els.forEach((e) => { if (e.getBoundingClientRect().top < w.innerHeight) e.classList.add('is-in'); }), 2500);
  }

  /* ---------- Lazy video ---------- */
  const small = () => w.matchMedia('(max-width: 749px)').matches || slowNet;
  function loadVideo(v) {
    if (v.dataset.state) return;
    const m = v.dataset.media;
    if (m && !w.matchMedia(m).matches) return;
    if (v.dataset.poster && !v.getAttribute('poster')) v.setAttribute('poster', v.dataset.poster);
    if (reduce || saveData) { v.dataset.state = 'poster'; return; }
    v.dataset.state = 'loaded';
    v.src = small() && v.dataset.srcSm ? v.dataset.srcSm : v.dataset.src;
    v.addEventListener('playing', () => v.classList.add('is-playing'), { once: true });
    if (v.hasAttribute('data-once')) {
      v.addEventListener('ended', () => { v.dataset.ended = '1'; });
    }
  }
  function play(v) {
    const p = v.play();
    if (p && p.catch) p.catch(() => {});
  }
  function initVideos(root = d) {
    const vids = $$('video[data-gf-video]', root).filter((v) => once(v, 'gfInit'));
    if (!vids.length) return;
    if (!hasIO) { vids.forEach((v) => { loadVideo(v); if (v.dataset.state === 'loaded') play(v); }); return; }
    const near = new IntersectionObserver((es) => es.forEach((e) => { if (e.isIntersecting) loadVideo(e.target); }), { rootMargin: '500px 0px' });
    const vis = new IntersectionObserver((es) => es.forEach((e) => {
      const v = e.target;
      if (v.dataset.state !== 'loaded') return;
      if (e.isIntersecting) {
        if (v.hasAttribute('data-once') && v.dataset.ended) { v.currentTime = 0; delete v.dataset.ended; }
        play(v);
      } else {
        v.pause();
      }
    }), { threshold: 0.2 });
    vids.forEach((v) => {
      if (v.hasAttribute('data-eager')) { loadVideo(v); if (v.dataset.state === 'loaded') play(v); }
      near.observe(v);
      vis.observe(v);
    });
    let t;
    w.addEventListener('resize', () => {
      clearTimeout(t);
      t = setTimeout(() => vids.forEach((v) => { if (!v.dataset.state && v.hasAttribute('data-eager')) { loadVideo(v); if (v.dataset.state === 'loaded') play(v); } }), 250);
    });
  }

  /* ---------- Explainer video: muted captioned preview on screen, tap restarts it with sound ---------- */
  function initExplainer(root = d) {
    $$('[data-gf-explainer]', root).filter((f) => once(f, 'gfInit')).forEach((f) => {
      const v = $('video', f);
      if (!v) return;
      const label = $('[data-gf-explainer-label]', f);
      const set = (m) => { f.dataset.mode = m; };
      const mode = () => f.dataset.mode;
      v.muted = true;
      v.removeAttribute('controls');
      const withSound = () => {
        if (mode() !== 'sound') v.currentTime = 0;
        set('sound');
        v.muted = false;
        v.controls = true;
        play(v);
      };
      $$('[data-gf-explainer-play], [data-gf-explainer-sound]', f).forEach((b) => b.addEventListener('click', withSound));
      v.addEventListener('click', () => { if (mode() !== 'sound') withSound(); });
      v.addEventListener('ended', () => {
        v.controls = false;
        if (label) label.textContent = I18N.watchAgain || 'Watch again';
        set('ended');
      });
      if (!hasIO || reduce || saveData || f.dataset.preview === 'false') return;
      new IntersectionObserver((es) => es.forEach((e) => {
        if (e.isIntersecting) {
          if (mode() !== 'idle') return;
          v.muted = true;
          const p = v.play();
          if (p && p.then) p.then(() => { if (mode() === 'idle') set('preview'); }).catch(() => {});
        } else if (!v.paused) {
          v.pause();
          if (mode() === 'preview') set('idle');
        }
      }), { threshold: 0.55 }).observe(v);
    });
  }

  /* ---------- Video stories: nothing loads until tapped; one plays at a time ---------- */
  function initStories(root = d) {
    $$('[data-gf-story]', root).filter((f) => once(f, 'gfInit')).forEach((f) => {
      const v = $('video', f);
      if (!v) return;
      const start = () => {
        $$('[data-gf-story]').forEach((o) => {
          const ov = $('video', o);
          if (o !== f && ov && !ov.paused) ov.pause();
        });
        f.dataset.mode = 'playing';
        v.controls = true;
        play(v);
      };
      $$('[data-gf-story-play]', f).forEach((b) => b.addEventListener('click', start));
      v.addEventListener('pause', () => { if (!v.ended) f.dataset.mode = 'paused'; });
      v.addEventListener('play', () => { f.dataset.mode = 'playing'; });
      v.addEventListener('ended', () => { v.controls = false; f.dataset.mode = 'idle'; });
      if (hasIO) new IntersectionObserver((es) => es.forEach((e) => { if (!e.isIntersecting && !v.paused) v.pause(); }), { threshold: 0.2 }).observe(f);
    });
  }

  /* ---------- Research library: topic filters + search ---------- */
  function initLibrary(root = d) {
    $$('[data-gf-library]', root).filter((el) => once(el, 'gfInit')).forEach((lib) => {
      const bar = $('[data-gf-library-bar]', lib);
      const input = $('[data-gf-library-search]', lib);
      const empty = $('[data-gf-library-empty]', lib);
      const items = $$('.gf-library__item', lib);
      const btns = $$('[data-gf-filter]', lib);
      if (bar) bar.hidden = false;
      let group = '';
      const apply = () => {
        const q = (input ? input.value : '').trim().toLowerCase();
        const words = q ? q.split(/\s+/) : [];
        let shown = 0;
        items.forEach((it) => {
          const okGroup = !group || it.dataset.group === group;
          const text = it.dataset.text || '';
          const okText = words.every((wd) => text.includes(wd));
          const show = okGroup && okText;
          it.hidden = !show;
          if (show) shown += 1;
        });
        if (empty) empty.hidden = shown > 0;
      };
      btns.forEach((b) => b.addEventListener('click', () => {
        group = b.dataset.gfFilter || '';
        btns.forEach((o) => { const on = o === b; o.classList.toggle('is-active', on); o.setAttribute('aria-pressed', on ? 'true' : 'false'); });
        apply();
      }));
      if (input) input.addEventListener('input', apply);
    });
  }

  /* ---------- Header + menu ---------- */
  function initHeader() {
    const h = $('[data-gf-header]');
    if (!h || !once(h, 'gfInit')) return;
    const onScroll = () => h.classList.toggle('is-scrolled', w.scrollY > 10);
    onScroll();
    w.addEventListener('scroll', onScroll, { passive: true });

    $$('.gf-header__sub', h).forEach((det) => det.addEventListener('toggle', () => {
      if (det.open) $$('.gf-header__sub', h).forEach((o) => { if (o !== det) o.open = false; });
    }));
    d.addEventListener('click', (e) => {
      if (!e.target.closest('.gf-header__sub')) $$('.gf-header__sub[open]', h).forEach((o) => { o.open = false; });
    });

    const menu = $('[data-gf-menu]');
    const btn = $('[data-gf-menu-open]');
    if (!menu || !btn) return;
    const open = () => {
      menu.hidden = false;
      h.classList.add('is-menu');
      requestAnimationFrame(() => requestAnimationFrame(() => menu.classList.add('is-open')));
      btn.setAttribute('aria-expanded', 'true');
      d.documentElement.classList.add('gf-lock');
      const f = $('[data-gf-menu-close]:not(.gf-menu__backdrop)', menu);
      if (f) f.focus();
    };
    const close = () => {
      menu.classList.remove('is-open');
      h.classList.remove('is-menu');
      btn.setAttribute('aria-expanded', 'false');
      d.documentElement.classList.remove('gf-lock');
      setTimeout(() => { menu.hidden = true; }, 380);
      btn.focus();
    };
    btn.addEventListener('click', open);
    $$('[data-gf-menu-close]', menu).forEach((b) => b.addEventListener('click', close));
    $$('a', menu).forEach((a) => a.addEventListener('click', () => { if ((a.getAttribute('href') || '').startsWith('#')) close(); }));
    menu.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') close();
      if (e.key === 'Tab') {
        const f = $$('a[href], button:not([disabled])', menu).filter((x) => x.offsetParent !== null);
        if (!f.length) return;
        if (e.shiftKey && d.activeElement === f[0]) { e.preventDefault(); f[f.length - 1].focus(); }
        else if (!e.shiftKey && d.activeElement === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
      }
    });
  }

  /* ---------- Announcement rotation (mobile) ---------- */
  function initAnnounce() {
    $$('[data-gf-announce]').forEach((t) => {
      const items = $$('.gf-announce__item', t);
      if (items.length < 2 || reduce || !once(t, 'gfInit')) return;
      let i = 0;
      setInterval(() => {
        items[i].classList.remove('is-active');
        i = (i + 1) % items.length;
        items[i].classList.add('is-active');
      }, 3800);
    });
  }

  /* ---------- Pointer: card spotlight + section glow ---------- */
  function initPointer() {
    if (!canHover || reduce) return;
    let ev = null;
    let queued = false;
    const apply = () => {
      queued = false;
      const t = ev.target;
      if (!t || !t.closest) return;
      const card = t.closest('[data-gf-spot-hover]');
      if (card) {
        const r = card.getBoundingClientRect();
        card.style.setProperty('--mx', `${ev.clientX - r.left}px`);
        card.style.setProperty('--my', `${ev.clientY - r.top}px`);
      }
      const fx = t.closest('.gf--fx');
      if (fx) {
        const r = fx.getBoundingClientRect();
        fx.style.setProperty('--gx', `${Math.round(((ev.clientX - r.left) / r.width) * 100)}%`);
      }
    };
    d.addEventListener('pointermove', (e) => {
      ev = e;
      if (!queued) { queued = true; requestAnimationFrame(apply); }
    }, { passive: true });
  }

  /* ---------- Grounding waves (canvas) ---------- */
  function initWaves(root = d) {
    $$('canvas[data-gf-waves]', root).forEach((c) => {
      if (!once(c, 'gfInit')) return;
      const ctx = c.getContext('2d');
      if (!ctx) return;
      const n = Math.max(1, parseInt(c.dataset.lines, 10) || 5);
      const amp = parseFloat(c.dataset.amp) || 0.22;
      const speed = parseFloat(c.dataset.speed) || 1;
      const glow = parseFloat(c.dataset.glow) || 1;
      const lines = Array.from({ length: n }, (_, i) => ({
        f: 1.05 + i * 0.17 + Math.random() * 0.2,
        p: Math.random() * Math.PI * 2,
        v: (0.32 + i * 0.045) * speed,
        a: 0.55 + 0.45 * Math.abs(Math.sin(i * 1.7 + 0.6)),
        o: (i - (n - 1) / 2) * 0.035,
      }));
      const parts = c.hasAttribute('data-particles')
        ? Array.from({ length: 34 }, () => ({ l: Math.floor(Math.random() * n), x: Math.random(), s: 0.03 + Math.random() * 0.07, r: 0.6 + Math.random() * 1.5, a: 0.35 + Math.random() * 0.6 }))
        : [];
      let W = 1;
      let H = 1;
      let D = 1;
      let core;
      let halo;
      let last = 0;
      // On light backgrounds additive blending vanishes, so draw deeper blues normally.
      const light = getComputedStyle(c).getPropertyValue('--gf-mode').trim() !== 'dark';
      const blend = light ? 'source-over' : 'lighter';
      const dot = light ? '47, 134, 201' : '200, 236, 255';
      const yAt = (L, x, t) => {
        const u = x / W;
        const env = 0.3 + 0.7 * Math.sin(Math.PI * u);
        const A = H * amp * L.a * env;
        return H * (0.55 + L.o) + A * Math.sin(Math.PI * 2 * L.f * u + L.p + t * L.v) + A * 0.22 * Math.sin(Math.PI * 2 * L.f * 2.3 * u - t * L.v * 1.4 + L.p);
      };
      const draw = (now) => {
        const t = now / 1000;
        const dt = last ? Math.min(0.05, t - last) : 0;
        last = t;
        ctx.clearRect(0, 0, W, H);
        ctx.globalCompositeOperation = blend;
        const step = Math.max(4, W / 180);
        lines.forEach((L, i) => {
          ctx.beginPath();
          for (let x = 0; x <= W + step; x += step) {
            const y = yAt(L, x, t);
            if (x === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
          }
          ctx.strokeStyle = halo;
          ctx.lineWidth = (7 + (i % 3) * 3) * D * glow;
          ctx.stroke();
          ctx.strokeStyle = core;
          ctx.lineWidth = (i % 2 ? 1 : 1.6) * D;
          ctx.stroke();
        });
        parts.forEach((p) => {
          p.x += p.s * dt;
          if (p.x > 1) { p.x = 0; p.l = Math.floor(Math.random() * n); }
          const x = p.x * W;
          const y = yAt(lines[p.l], x, t);
          const fade = Math.sin(Math.PI * p.x);
          ctx.beginPath();
          ctx.arc(x, y, p.r * D, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${dot}, ${(p.a * fade * (light ? 0.7 : 1)).toFixed(3)})`;
          ctx.fill();
        });
        ctx.globalCompositeOperation = 'source-over';
      };
      fitCanvas(c, (cw, ch, dpr) => {
        W = cw; H = ch; D = dpr;
        core = ctx.createLinearGradient(0, 0, W, 0);
        halo = ctx.createLinearGradient(0, 0, W, 0);
        if (light) {
          core.addColorStop(0, 'rgba(61, 127, 82, 0)');
          core.addColorStop(0.18, 'rgba(61, 127, 82, 0.45)');
          core.addColorStop(0.5, 'rgba(44, 90, 59, 0.7)');
          core.addColorStop(0.82, 'rgba(61, 127, 82, 0.45)');
          core.addColorStop(1, 'rgba(61, 127, 82, 0)');
          halo.addColorStop(0, 'rgba(127, 207, 150, 0)');
          halo.addColorStop(0.5, `rgba(127, 207, 150, ${0.1 * glow})`);
          halo.addColorStop(1, 'rgba(127, 207, 150, 0)');
        } else {
          core.addColorStop(0, 'rgba(44, 90, 59, 0)');
          core.addColorStop(0.18, 'rgba(127, 207, 150, 0.55)');
          core.addColorStop(0.5, 'rgba(205, 238, 213, 0.95)');
          core.addColorStop(0.82, 'rgba(127, 207, 150, 0.55)');
          core.addColorStop(1, 'rgba(44, 90, 59, 0)');
          halo.addColorStop(0, 'rgba(44, 90, 59, 0)');
          halo.addColorStop(0.5, `rgba(127, 207, 150, ${0.07 * glow})`);
          halo.addColorStop(1, 'rgba(44, 90, 59, 0)');
        }
        draw(performance.now());
      });
      loop(c, draw);
    });
  }

  /* ---------- Body-voltage oscilloscope ---------- */
  function initScope(root = d) {
    $$('[data-gf-scope]', root).forEach((m) => {
      if (!once(m, 'gfInit')) return;
      const canvas = $('[data-gf-scope-canvas]', m);
      const valEl = $('[data-gf-scope-value]', m);
      const deltaEl = $('[data-gf-scope-delta]', m);
      const stateEl = $('[data-gf-scope-state]', m);
      const btns = $$('[data-gf-scope-btn]', m);
      const off = parseFloat(m.dataset.off) || 2.32;
      const on = parseFloat(m.dataset.on) || 0.04;
      const labels = { off: m.dataset.labelOff || I18N.ungrounded || 'Ungrounded', on: m.dataset.labelOn || I18N.grounded || 'Grounded' };
      let cur = off;
      let from = off;
      let target = off;
      let t0 = 0;
      let touched = false;
      const ctx = canvas && canvas.getContext('2d');
      let W = 1;
      let H = 1;
      let D = 1;
      const mix = (a, b, k) => Math.round(a + (b - a) * k);
      const render = (now) => {
        const t = now / 1000;
        if (t0) {
          const k = Math.min(1, (now - t0) / (reduce ? 1 : 1600));
          const e = 1 - Math.pow(1 - k, 4);
          cur = from + (target - from) * e;
          if (k >= 1) t0 = 0;
        }
        const jitter = t0 || reduce ? 0 : (Math.sin(t * 7.3) + Math.sin(t * 3.1)) * (target === off ? 0.012 : 0.0015);
        const shown = Math.max(0.01, cur + jitter);
        valEl.textContent = shown.toFixed(2);
        const g = Math.max(0, Math.min(1, (off - cur) / (off - on)));
        if (!ctx) return;
        // Screen: 10 x 8 divisions at 1 V / div
        ctx.clearRect(0, 0, W, H);
        const dx = W / 10;
        const dy = H / 8;
        ctx.lineWidth = D;
        ctx.strokeStyle = 'rgba(168, 215, 180, 0.08)';
        ctx.beginPath();
        for (let i = 1; i < 10; i++) { ctx.moveTo(i * dx, 0); ctx.lineTo(i * dx, H); }
        for (let j = 1; j < 8; j++) { ctx.moveTo(0, j * dy); ctx.lineTo(W, j * dy); }
        ctx.stroke();
        ctx.strokeStyle = 'rgba(168, 215, 180, 0.2)';
        ctx.beginPath();
        ctx.moveTo(0, H / 2); ctx.lineTo(W, H / 2);
        ctx.moveTo(W / 2, 0); ctx.lineTo(W / 2, H);
        ctx.stroke();
        const col = `${mix(255, 92, g)}, ${mix(138, 188, g)}, ${mix(101, 255, g)}`;
        const A = Math.min(cur, 3.8) * dy;
        const ph = reduce ? 0 : t * 5.2;
        const step = Math.max(2, W / 260);
        ctx.beginPath();
        for (let x = 0; x <= W + step; x += step) {
          const u = x / W;
          const noise = Math.sin(u * 91 + t * 13) * 0.05 + Math.sin(u * 37 - t * 9) * 0.04;
          const y = H / 2 - (A * Math.sin(Math.PI * 2 * 3 * u - ph) + dy * noise * (0.25 + (1 - g)));
          if (x === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
        }
        ctx.globalCompositeOperation = 'lighter';
        ctx.strokeStyle = `rgba(${col}, 0.16)`;
        ctx.lineWidth = 9 * D;
        ctx.stroke();
        ctx.strokeStyle = `rgba(${col}, 0.95)`;
        ctx.lineWidth = 2 * D;
        ctx.stroke();
        ctx.globalCompositeOperation = 'source-over';
      };
      const set = (s) => {
        btns.forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.gfScopeBtn === s)));
        m.classList.toggle('is-grounded', s === 'on');
        if (stateEl) stateEl.textContent = labels[s];
        from = cur;
        target = s === 'on' ? on : off;
        t0 = performance.now();
        if (deltaEl) deltaEl.textContent = s === 'on' ? (I18N.timesLower || '__N__× lower').replace('__N__', Math.round(off / on)) : '—';
        if (reduce) render(performance.now() + 2000);
      };
      btns.forEach((b) => b.addEventListener('click', () => { touched = true; set(b.dataset.gfScopeBtn); }));
      if (canvas) {
        fitCanvas(canvas, (cw, ch, dpr) => { W = cw; H = ch; D = dpr; render(performance.now()); });
        loop(canvas, render);
      }
      if (hasIO) {
        const io = new IntersectionObserver((es) => es.forEach((e) => {
          if (!e.isIntersecting) return;
          io.disconnect();
          setTimeout(() => { if (!touched) set('on'); }, 1800);
        }), { threshold: 0.55 });
        io.observe(m);
      }
    });
  }

  /* ---------- Counting numbers ---------- */
  function initCounters(root = d) {
    const els = $$('[data-gf-counter]', root).filter((el) => once(el, 'gfInit'));
    if (!els.length || !hasIO || reduce) return;
    const parse = (el) => {
      const raw = String(el.dataset.gfCounter || '').trim();
      const num = parseFloat(raw.replace(/,/g, ''));
      const dec = (raw.split('.')[1] || '').replace(/\D/g, '').length;
      const grouped = /,/.test(raw);
      const fmt = (v) => (grouped ? v.toLocaleString(LANG, { minimumFractionDigits: dec, maximumFractionDigits: dec }) : v.toFixed(dec));
      return { num, fmt, pre: el.dataset.prefix || '', suf: el.dataset.suffix || '' };
    };
    const run = (el) => {
      const { num, fmt, pre, suf } = parse(el);
      const start = performance.now();
      const step = (now) => {
        const k = Math.min(1, (now - start) / 1700);
        const e = 1 - Math.pow(1 - k, 3);
        el.textContent = pre + fmt(num * e) + suf;
        if (k < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    };
    const io = new IntersectionObserver((es) => es.forEach((e) => {
      if (!e.isIntersecting) return;
      io.unobserve(e.target);
      run(e.target);
    }), { threshold: 0.6 });
    els.forEach((el) => {
      const { num, fmt, pre, suf } = parse(el);
      if (!isFinite(num) || num === 0) return;
      // Only count up numbers that start below the fold; visible ones stay put.
      if (el.getBoundingClientRect().top < w.innerHeight * 0.9) return;
      el.textContent = pre + fmt(0) + suf;
      io.observe(el);
    });
  }

  /* ---------- Statement word highlight ---------- */
  function initWords() {
    $$('[data-gf-words]').forEach((el) => {
      if (!once(el, 'gfInit')) return;
      const words = $$('.gf-word', el);
      if (!words.length) return;
      if (reduce) { words.forEach((x) => x.classList.add('is-lit')); return; }
      let ticking = false;
      const update = () => {
        ticking = false;
        const r = el.getBoundingClientRect();
        const vh = w.innerHeight;
        const start = vh * 0.9;
        const end = vh * 0.3 - r.height * 0.4;
        const prog = Math.min(1, Math.max(0, (start - r.top) / (start - end)));
        const lit = Math.round(prog * words.length);
        words.forEach((x, i) => x.classList.toggle('is-lit', i < lit));
      };
      w.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
      update();
    });
  }

  /* ---------- Scroll story ---------- */
  function initStory(root = d) {
    $$('[data-gf-story]', root).forEach((s) => {
      if (!once(s, 'gfInit')) return;
      const steps = $$('[data-gf-story-step]', s);
      const imgs = $$('[data-gf-story-img]', s);
      const nEl = $('[data-gf-story-n]', s);
      const bar = $('[data-gf-story-bar]', s);
      const kEl = $('[data-gf-story-k]', s);
      if (!steps.length || !hasIO) return;
      const set = (i) => {
        if (i < 0) return;
        steps.forEach((st, k) => st.classList.toggle('is-active', k === i));
        imgs.forEach((im, k) => im.classList.toggle('is-active', k === i));
        if (nEl) nEl.textContent = String(i + 1).padStart(2, '0');
        if (bar) bar.style.setProperty('--p', String((i + 1) / steps.length));
        if (kEl) kEl.textContent = steps[i].dataset.kicker || '';
      };
      const io = new IntersectionObserver((es) => es.forEach((e) => {
        if (e.isIntersecting) set(steps.indexOf(e.target));
      }), { rootMargin: '-46% 0px -46% 0px' });
      steps.forEach((st) => io.observe(st));
    });
  }

  /* ---------- Tabs ---------- */
  function initTabs(root = d) {
    $$('[data-gf-tabs]', root).forEach((t) => {
      if (!once(t, 'gfInit')) return;
      const list = $('[role="tablist"]', t);
      const tabs = $$('[role="tab"]', t);
      const select = (i, focus) => {
        tabs.forEach((b, k) => {
          const on = k === i;
          b.setAttribute('aria-selected', String(on));
          b.tabIndex = on ? 0 : -1;
          const p = d.getElementById(b.getAttribute('aria-controls'));
          if (p) p.hidden = !on;
        });
        const b = tabs[i];
        if (focus) b.focus({ preventScroll: true });
        if (list && list.scrollWidth > list.clientWidth + 2) {
          list.scrollTo({ left: b.offsetLeft - (list.clientWidth - b.offsetWidth) / 2, behavior: reduce ? 'auto' : 'smooth' });
        }
      };
      tabs.forEach((b, i) => {
        b.addEventListener('click', () => select(i, false));
        b.addEventListener('keydown', (e) => {
          let n = null;
          if (e.key === 'ArrowRight') n = (i + 1) % tabs.length;
          if (e.key === 'ArrowLeft') n = (i - 1 + tabs.length) % tabs.length;
          if (e.key === 'Home') n = 0;
          if (e.key === 'End') n = tabs.length - 1;
          if (n !== null) { e.preventDefault(); select(n, true); }
        });
      });
    });
  }

  /* ---------- All-reviews panel ---------- */
  function initLoox(root = d) {
    $$('[data-gf-loox]', root).forEach((box) => {
      if (!once(box, 'gfInit')) return;
      const inner = $('.gf-loox__inner', box);
      const btn = $('[data-gf-loox-open]', box);
      const open = () => box.classList.add('is-open');
      if (btn) btn.addEventListener('click', () => { open(); btn.blur(); });
      // Short widgets (few reviews) don't need the fold. Measured against the
      // collapsed height, so a widget that grows after loading folds again.
      if (inner && hasRO) {
        const fold = parseFloat(getComputedStyle(inner).maxHeight) || 560;
        const ro = new ResizeObserver(() => {
          const content = inner.firstElementChild ? inner.firstElementChild.getBoundingClientRect().height : inner.scrollHeight;
          box.classList.toggle('is-short', content > 0 && content <= fold + 40);
        });
        ro.observe(inner);
        if (inner.firstElementChild) ro.observe(inner.firstElementChild);
      }
    });
  }

  /* ---------- Read more (mobile clamp) ---------- */
  function initClamp(root = d) {
    $$('[data-gf-clamp]', root).forEach((box) => {
      if (!once(box, 'gfInit')) return;
      const btn = $('[data-gf-clamp-btn]', box);
      const body = $('.gf-clamp__body', box);
      if (!btn || !body) return;
      // Hide the toggle when the text fits anyway.
      const check = () => {
        const clamped = box.classList.contains('is-closed');
        if (clamped && body.scrollHeight <= body.clientHeight + 8) btn.hidden = true;
      };
      btn.addEventListener('click', () => {
        const open = box.classList.toggle('is-closed') === false;
        btn.setAttribute('aria-expanded', String(open));
        $('[data-more]', btn).hidden = open;
        $('[data-less]', btn).hidden = !open;
      });
      if (hasRO) new ResizeObserver(check).observe(body); else check();
    });
  }

  /* ---------- Rail dots + auto-slide (mobile carousels) ---------- */
  function initRailDots(root = d) {
    $$('[data-gf-rail][data-dots]', root).forEach((rail) => {
      if (!once(rail, 'gfDots')) return;
      const items = Array.from(rail.children).filter((el) => getComputedStyle(el).display !== 'none');
      if (items.length < 2) return;
      const dots = d.createElement('div');
      dots.className = 'gf-dots';
      let current = 0;
      let userTouched = false;
      const goTo = (i, smooth = true) => {
        rail.scrollTo({ left: items[i].offsetLeft - rail.offsetLeft, behavior: smooth && !reduce ? 'smooth' : 'auto' });
      };
      items.forEach((_, i) => {
        const b = d.createElement('button');
        b.type = 'button';
        b.setAttribute('aria-label', `Go to item ${i + 1}`);
        if (i === 0) b.classList.add('is-active');
        b.addEventListener('click', () => { userTouched = true; goTo(i); });
        dots.appendChild(b);
      });
      rail.after(dots);
      const mark = (i) => {
        current = i;
        Array.from(dots.children).forEach((b, k) => b.classList.toggle('is-active', k === i));
      };
      if (hasIO) {
        const io = new IntersectionObserver((es) => {
          es.forEach((e) => { if (e.isIntersecting) mark(items.indexOf(e.target)); });
        }, { root: rail, threshold: 0.6 });
        items.forEach((it) => io.observe(it));
      }
      // Auto-advance while on screen, until the visitor takes over.
      const every = parseInt(rail.dataset.autoslide, 10);
      if (every && !reduce) {
        let onScreen = false;
        ['pointerdown', 'wheel', 'touchstart'].forEach((ev) => rail.addEventListener(ev, () => { userTouched = true; }, { passive: true }));
        if (hasIO) new IntersectionObserver((es) => { onScreen = es[0].isIntersecting; }, { threshold: 0.35 }).observe(rail);
        setInterval(() => {
          if (userTouched || !onScreen || d.hidden) return;
          if (rail.scrollWidth <= rail.clientWidth + 40) return; // not a slider right now
          goTo((current + 1) % items.length);
        }, every);
      }
    });
  }

  /* ---------- Contact-time calculator ---------- */
  function initMath(root = d) {
    $$('[data-gf-math]', root).forEach((m) => {
      if (!once(m, 'gfInit')) return;
      const input = $('input[type=range]', m);
      const matWeek = (parseFloat(m.dataset.matHours) || 8) * 7;
      const minEl = $('[data-gf-math-min]', m);
      const outEl = $('[data-gf-math-out]', m);
      const matEl = $('[data-gf-math-mat]', m);
      const barOut = $('[data-gf-math-bar-out]', m);
      const barMat = $('[data-gf-math-bar-mat]', m);
      const mult = $('[data-gf-math-mult]', m);
      const fmt = (h) => (h >= 10 ? Math.round(h) : Math.round(h * 10) / 10).toString();
      const update = () => {
        const v = +input.value;
        const week = (v * 7) / 60;
        minEl.textContent = v;
        outEl.textContent = fmt(week);
        matEl.textContent = fmt(matWeek);
        const mx = Math.max(matWeek, week);
        barOut.style.transform = `scaleX(${Math.max(0.012, week / mx)})`;
        barMat.style.transform = `scaleX(${matWeek / mx})`;
        mult.textContent = week > 0 ? `${Math.round(matWeek / week)}×` : '∞';
        input.style.setProperty('--p', `${((v - input.min) / (input.max - input.min)) * 100}%`);
      };
      input.addEventListener('input', update);
      update();
    });
  }

  /* ---------- Hotspots ---------- */
  function initHotspots(root = d) {
    $$('[data-gf-hotspots]', root).forEach((hs) => {
      if (!once(hs, 'gfInit')) return;
      const spots = $$('[data-gf-spot]', hs);
      const section = hs.closest('.gf-hw') || hs;
      const set = (s) => spots.forEach((x) => {
        const on = x === s && !x.classList.contains('is-active');
        x.classList.toggle('is-active', on);
        $('button', x).setAttribute('aria-expanded', String(on));
      });
      spots.forEach((s) => $('button', s).addEventListener('click', (e) => { e.stopPropagation(); set(s); }));
      d.addEventListener('click', (e) => { if (!hs.contains(e.target)) set(null); });
      $$('[data-gf-spot-link]', section).forEach((li) => {
        const i = +li.dataset.gfSpotLink;
        li.addEventListener('mouseenter', () => spots[i] && spots[i].classList.add('is-hl'));
        li.addEventListener('mouseleave', () => spots[i] && spots[i].classList.remove('is-hl'));
      });
    });
  }

  /* ---------- Gallery ---------- */
  function initGallery(root) {
    const track = $('[data-gf-track]', root);
    if (!track || !once(root, 'gfInit')) return;
    const slides = $$('.gf-gallery__slide', track);
    const dots = $$('[data-gf-dot]', root);
    const count = $('[data-gf-index]', root);
    const prev = $('[data-gf-prev]', root);
    const next = $('[data-gf-next]', root);
    let idx = 0;
    const go = (i) => {
      i = Math.max(0, Math.min(slides.length - 1, i));
      track.scrollTo({ left: i * track.clientWidth, behavior: reduce ? 'auto' : 'smooth' });
    };
    const mark = (i) => {
      idx = i;
      dots.forEach((dt, k) => dt.classList.toggle('is-active', k === i));
      if (count) count.textContent = String(i + 1);
      if (prev) prev.disabled = i === 0;
      if (next) next.disabled = i === slides.length - 1;
    };
    let t;
    track.addEventListener('scroll', () => {
      clearTimeout(t);
      t = setTimeout(() => mark(Math.round(track.scrollLeft / Math.max(1, track.clientWidth))), 60);
    }, { passive: true });
    dots.forEach((dt) => dt.addEventListener('click', () => go(+dt.dataset.gfDot)));
    if (prev) prev.addEventListener('click', () => go(idx - 1));
    if (next) next.addEventListener('click', () => go(idx + 1));
    track.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') { e.preventDefault(); go(idx + 1); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); go(idx - 1); }
    });
    mark(0);
  }

  /* ---------- Product page ---------- */
  const PLUG_BY_COUNTRY = {
    US: 'US', CA: 'US', MX: 'US', PR: 'US', JP: 'US', PH: 'US', TW: 'US',
    GB: 'UK', IE: 'UK', SG: 'UK', HK: 'UK', MY: 'UK', AE: 'UK', SA: 'UK', QA: 'UK', CY: 'UK', MT: 'UK',
    AU: 'AU', NZ: 'AU', FJ: 'AU',
  };
  const EU_LIKE = ['AT','BE','BG','HR','CZ','DK','EE','FI','FR','DE','GR','HU','IS','IT','LV','LT','LU','NL','NO','PL','PT','RO','SK','SI','ES','SE','CH','RS','BA','ME','MK','AL','UA','MD','TR','IL','KR','RU','BY','GE','AM','AZ','KZ','ZA','BR','AR','CL','ID','VN','TH','EG','MA'];

  function initPdp(root) {
    const jsonEl = $('[data-gf-product]', root);
    const form = $('[data-gf-form]', root);
    if (!jsonEl || !form || !once(root, 'gfInit')) return;
    const product = JSON.parse(jsonEl.textContent);
    const idInput = $('[data-gf-variant-id]', form);
    const fieldsets = $$('[data-gf-option]', form);
    const atc = $('[data-gf-atc]', form);
    const atcText = $('[data-gf-atc-text]', form);
    const atcPrice = $('[data-gf-atc-price]', form);
    const err = $('[data-gf-error]', form);
    const sticky = $('[data-gf-sticky]', root);
    const atcLabel = atcText ? atcText.textContent : I18N.addToCart || 'Add to cart';
    const optNames = product.options.map((o) => (typeof o === 'string' ? o : o.name));
    const plugName = (root.dataset.plugOption || '').trim();
    const plugIdx = optNames.findIndex((n) => n.toLowerCase() === plugName.toLowerCase());

    const selected = () => fieldsets.map((fs) => { const c = $('input:checked', fs); return c ? c.value : null; });
    // Single-variant products render no option fieldsets, so fall back to the form's own variant.
    const findVariant = (vals) => (fieldsets.length
      ? product.variants.find((v) => v.options.every((o, i) => o === vals[i]))
      : product.variants.find((v) => String(v.id) === String(idInput.value)) || product.variants[0]);
    const pretty = (t) => t.replace(/x(\d)/gi, ' × $1').replace(/(\d)CM\b/g, '$1 cm').replace(/\s*\/\s*/g, ' · ').replace(/ - /g, ' — ');

    const updateOptionPrices = (vals) => {
      fieldsets.forEach((fs, oi) => {
        $$('.gf-opt__item', fs).forEach((item) => {
          const input = $('input', item);
          const test = vals.slice();
          test[oi] = input.value;
          const v = findVariant(test);
          item.classList.toggle('is-unavailable', !v || !v.available);
          const priceEl = $('[data-gf-opt-price]', item);
          if (priceEl && v) priceEl.textContent = money(v.price);
        });
        const sel = $('[data-gf-opt-selected]', fs);
        if (sel) sel.textContent = (vals[oi] || '').split(' - ')[0].replace(/\s*\/\s*/g, ' / ');
      });
    };

    // Quantity stepper
    const qtyInput = $('[data-gf-qty]', form);
    const qtyClamp = () => { if (qtyInput) qtyInput.value = Math.min(99, Math.max(1, parseInt(qtyInput.value, 10) || 1)); };
    const qm = $('[data-gf-qty-minus]', form);
    const qp = $('[data-gf-qty-plus]', form);
    if (qm) qm.addEventListener('click', () => { qtyInput.value = (parseInt(qtyInput.value, 10) || 1) - 1; qtyClamp(); });
    if (qp) qp.addEventListener('click', () => { qtyInput.value = (parseInt(qtyInput.value, 10) || 1) + 1; qtyClamp(); });
    if (qtyInput) qtyInput.addEventListener('change', qtyClamp);

    // Real-stock badge: reads tracked inventory, hides otherwise
    let inv = null;
    const invEl = $('[data-gf-inv]', root);
    if (invEl) { try { inv = JSON.parse(invEl.textContent); } catch (x) { inv = null; } }
    const stockBadge = $('[data-gf-stock]', form);
    const updateStock = (variant) => {
      if (!stockBadge || !inv || !variant) return;
      const q = inv.v ? inv.v[String(variant.id)] : null;
      if (typeof q !== 'number') return; // untracked: keep the merchant's static badge as written
      const show = q > 0 && q <= (inv.t || 15);
      stockBadge.hidden = !show;
      if (show) { const b = $('[data-gf-stock-text]', stockBadge); if (b) b.textContent = (I18N.unitsLeft || 'Only __Q__ units left').replace('__Q__', q); }
    };

    const render = (variant, pushUrl) => {
      const priceEl = $('[data-gf-price]', form);
      const was = $('[data-gf-was]', form);
      const save = $('[data-gf-save]', form);
      if (!variant) {
        atc.disabled = true;
        atcText.textContent = I18N.unavailable || 'Unavailable';
        return;
      }
      idInput.value = variant.id;
      priceEl.textContent = money(variant.price);
      const showWas = variant.compare_at_price && variant.compare_at_price > variant.price && variant.compare_at_price / variant.price < 2.5;
      was.hidden = !showWas;
      save.hidden = !showWas;
      if (showWas) {
        was.textContent = money(variant.compare_at_price);
        save.textContent = (I18N.save || 'Save __A__').replace('__A__', moneyShort(variant.compare_at_price - variant.price));
      }
      updateStock(variant);
      atc.disabled = !variant.available;
      atcText.textContent = variant.available ? atcLabel : I18N.soldOut || 'Sold out';
      if (atcPrice) atcPrice.textContent = money(variant.price);
      if (sticky) {
        const sv = $('[data-gf-sticky-variant]', sticky);
        const sp = $('[data-gf-sticky-price]', sticky);
        if (sv && product.variants.length > 1) sv.textContent = pretty(variant.title);
        if (sp) sp.textContent = money(variant.price);
      }
      if (pushUrl && w.history.replaceState) {
        const u = new URL(w.location.href);
        u.searchParams.set('variant', variant.id);
        w.history.replaceState({}, '', u.toString());
      }
    };

    const onChange = (push = true) => {
      const vals = selected();
      updateOptionPrices(vals);
      render(findVariant(vals), push);
    };
    fieldsets.forEach((fs) => fs.addEventListener('change', () => onChange(true)));

    // Auto-select the visitor's plug type (only when no variant was requested in the URL)
    if (plugIdx >= 0 && !new URLSearchParams(w.location.search).has('variant')) {
      const cc = (root.dataset.country || '').toUpperCase();
      const plug = PLUG_BY_COUNTRY[cc] || (EU_LIKE.includes(cc) ? 'EU' : null);
      const fs = fieldsets[plugIdx];
      if (plug && fs) {
        const input = $$('input', fs).find((i) => i.value.toUpperCase() === plug);
        if (input && !input.checked) { input.checked = true; }
      }
    }
    onChange(false);
    if (sticky) {
      const sv = $('[data-gf-sticky-variant]', sticky);
      const v = findVariant(selected());
      if (sv && v && product.variants.length > 1) sv.textContent = pretty(v.title);
    }

    // Add to cart (main item + optional add-ons), then open Dawn's cart drawer
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (atc.disabled || atc.classList.contains('is-loading')) return;
      err.hidden = true;
      atc.classList.add('is-loading');
      const qtyEl = $('[data-gf-qty]', form);
      const qty = Math.min(99, Math.max(1, parseInt(qtyEl && qtyEl.value, 10) || 1));
      const items = [{ id: Number(idInput.value), quantity: qty }];
      const drawer = d.querySelector('cart-drawer');
      const body = { items };
      if (drawer && drawer.getSectionsToRender) {
        body.sections = drawer.getSectionsToRender().map((s) => s.id).join(',');
        body.sections_url = w.location.pathname;
      }
      try {
        const res = await fetch(`${(w.routes && w.routes.cart_add_url) || '/cart/add'}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify(body),
        });
        const data = await res.json();
        if (!res.ok || data.status) {
          err.textContent = data.description || data.message || 'Something went wrong. Please try again.';
          err.hidden = false;
          return;
        }
        if (typeof w.publish === 'function' && w.PUB_SUB_EVENTS) {
          try { w.publish(w.PUB_SUB_EVENTS.cartUpdate, { source: 'gf-product', productVariantId: items[0].id, cartData: data }); } catch (x) { /* noop */ }
        }
        d.dispatchEvent(new CustomEvent('gf:cart:added', { detail: { items } }));
        if (drawer && data.sections) {
          drawer.classList.remove('is-empty');
          drawer.renderContents({ id: items[0].id, sections: data.sections });
        } else {
          w.location.href = (w.routes && w.routes.cart_url) || '/cart';
        }
        atc.classList.add('is-added');
        setTimeout(() => atc.classList.remove('is-added'), 1600);
      } catch (x) {
        form.submit();
      } finally {
        atc.classList.remove('is-loading');
      }
    });

    // Sticky add-to-cart
    if (sticky && hasIO) {
      const btn = $('[data-gf-sticky-btn]', sticky);
      const io = new IntersectionObserver((es) => es.forEach((en) => {
        const show = !en.isIntersecting && en.boundingClientRect.top < 0;
        sticky.classList.toggle('is-visible', show);
        sticky.setAttribute('aria-hidden', String(!show));
        btn.tabIndex = show ? 0 : -1;
        d.documentElement.classList.toggle('gf-has-sticky', show);
      }));
      io.observe(atc);
      btn.addEventListener('click', () => {
        if (form.requestSubmit) form.requestSubmit(); else atc.click();
      });
    }

    // Delivery estimate (business days)
    $$('[data-gf-eta]', root).forEach((el) => {
      const min = parseInt(el.dataset.min, 10);
      const max = parseInt(el.dataset.max, 10);
      if (!min || !max) return;
      const add = (n) => {
        const dt = new Date();
        let added = 0;
        while (added < n) {
          dt.setDate(dt.getDate() + 1);
          const day = dt.getDay();
          if (day !== 0 && day !== 6) added += 1;
        }
        return dt;
      };
      const f = new Intl.DateTimeFormat(LANG, { month: 'short', day: 'numeric' });
      el.textContent = `${f.format(add(min))} – ${f.format(add(max))}`;
    });
  }

  /* ---------- Boot ---------- */
  function initAll(root = d) {
    initReveal(root);
    initVideos(root);
    initExplainer(root);
    initStories(root);
    initLibrary(root);
    initWaves(root);
    initScope(root);
    initCounters(root);
    initWords();
    initStory(root);
    initTabs(root);
    initLoox(root);
    initClamp(root);
    initRailDots(root);
    initMath(root);
    initHotspots(root);
    $$('[data-gf-gallery]', root).forEach(initGallery);
    $$('[data-gf-pdp]', root).forEach(initPdp);
  }
  function boot() {
    initHeader();
    initAnnounce();
    initPointer();
    initAll();
  }
  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', boot); else boot();

  // Gallery video posters (data-poster) wait for the load event so they don't compete with the hero image,
  // but are ready before a shopper swipes to them (the near-viewport observer only watches vertically).
  const galleryPosters = () => $$('.gf-gallery video[data-poster]').forEach((v) => {
    if (!v.getAttribute('poster')) v.setAttribute('poster', v.dataset.poster);
  });
  if (d.readyState === 'complete') galleryPosters(); else w.addEventListener('load', galleryPosters, { once: true });

  // Footer country / language selector: switch on change
  d.addEventListener('change', (e) => {
    const sel = e.target.closest && e.target.closest('[data-gf-locale]');
    if (sel && sel.form) sel.form.submit();
  });

  // Theme editor: re-init a section after it re-renders
  d.addEventListener('shopify:section:load', (e) => {
    if (e.target.querySelector('[data-gf-header]')) initHeader();
    initAll(e.target);
  });

  // ---- Cart drawer upsells: one-click add, then re-render the drawer (Dawn sections API)
  d.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-gf-upsell-add]');
    if (!btn || btn.classList.contains('is-loading')) return;
    const id = Number(btn.dataset.variantId);
    if (!id) return;
    const drawer = d.querySelector('cart-drawer');
    btn.classList.add('is-loading');
    const label = btn.querySelector('span:last-child');
    const old = label ? label.textContent : '';
    if (label) label.textContent = I18N.adding || 'Adding';
    const body = { items: [{ id, quantity: 1 }] };
    if (drawer && drawer.getSectionsToRender) {
      body.sections = drawer.getSectionsToRender().map((s) => s.id).join(',');
      body.sections_url = w.location.pathname;
    }
    try {
      const res = await fetch(`${(w.routes && w.routes.cart_add_url) || '/cart/add'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (res.ok && !data.status && drawer && data.sections) {
        if (typeof w.publish === 'function' && w.PUB_SUB_EVENTS) {
          try { w.publish(w.PUB_SUB_EVENTS.cartUpdate, { source: 'gf-drawer-upsell', productVariantId: id, cartData: data }); } catch (x) { /* noop */ }
        }
        drawer.classList.remove('is-empty');
        drawer.renderContents({ id, sections: data.sections });
      }
    } catch (x) { /* keep the drawer usable */ } finally {
      btn.classList.remove('is-loading');
      if (label) label.textContent = old;
    }
  });

})();
